import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject, Observable } from 'rxjs';
import { concatMap, takeUntil, tap } from 'rxjs/operators';

import { SelectBox } from 'cbc-shared-lib';

import {
  Sgkd034aResponse,
  Sgkd004aResponse,
  CodeConversionMstDto
} from 'sg-api';
import {
  CodeConversionMstService
} from '../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';
import { SgAriaMessage } from '../../../../../sg-base/sg-base-shared/models/sg-aria-message';
import { Sgkz004dComponent } from '../../../../../sg-base/sg-base-shared/pages/sgkz004d/components/sgkz004d.component';
import { CompleteDataService } from '../../../../../sg-base/sg-base-shared/pages/sgkz003d/services/complete-data.service';
import { SgKdConstants } from '../../../../sg-kd-shared/sg-kd-constants';
import { UsedSzza012aService } from '../../../../sg-kd-shared/services/used-szza012a.service';
import { UsedSgkd002dSgkd034aService } from '../services/used-sgkd002d-sgkd034a.service';
import { UsedSgkd002dSgkd004aService } from '../services/used-sgkd002d-sgkd004a.service';
import { UsedSgkd002dSgkd005aService } from '../services/used-sgkd002d-sgkd005a.service';
import { UsedSgkd002dSgkd044aService } from '../services/used-sgkd002d-sgkd044a.service';
import { UsedSgkd002dSgkd048aService } from '../services/used-sgkd002d-sgkd048a.service';

@Component({
  selector: 'app-sgkd002d',
  templateUrl: './sgkd002d.component.html',
  styleUrl: './sgkd002d.component.scss'
})
export class Sgkd002dComponent implements OnInit, OnDestroy {

  private onDestroy$ = new Subject();

  /** 請求お客さま番号 */
  paramClaimCtrNo: string;
  /** 内部お客さま番号 */
  paramInsideVisitNo: string;

  /** レスポンス */
  response: Sgkd034aResponse;

  /** 初期化フラグ_請求情報取得 */
  selectedSeikyuInfo: boolean = false;
  /** 初期化フラグ_コード読込 */
  loadedCodeList: boolean = false;
  /** 初期化フラグ_クレジット企業コード読込 */
  loadedCompanycodeInfoList: boolean = false;

  /** メインフォーム */
  mainForm: UntypedFormGroup;

  /** 支払方法コードリスト */
  codePayMethodList: SelectBox[];
  /** 預金種別コードリスト */
  codeDptClsfyCdList: SelectBox[];
  /** 受取方法コードリスト */
  codeRecivMethodList: SelectBox[];
  /** クレ企業名コードリスト */
  codeCreComAnameList: SelectBox[];

  /** 業務日付 */
  businessDate: string;

  /** 受付可能フラグ */ 
  aceptAbleFlg: boolean = false;

  /** 受付番号 */
  aceptNo?: string;

  errorMessageModelArray: Array<SgAriaMessage> = [];

  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private usedSgkd002dSgkd034aService: UsedSgkd002dSgkd034aService,
    private codeConversionMstService: CodeConversionMstService,
    private usedSgkd002dSgkd004aService: UsedSgkd002dSgkd004aService,
    private usedSgkd002dSgkd005aService: UsedSgkd002dSgkd005aService,
    private usedSzza012aService: UsedSzza012aService,
    private completeDataService: CompleteDataService,
    public dialog: MatDialog,
    private router: Router,
    private usedSgkd002dSgkd048aService: UsedSgkd002dSgkd048aService,
    private usedSgkd002dSgkd044aService: UsedSgkd002dSgkd044aService) {}

  /**
   * 初期処理
   * 
   * @memberof Sgkd002dComponent
   */
  ngOnInit(): void {

    // リクエストパラメタ読込
    this.paramClaimCtrNo = this.route.snapshot.paramMap.get('claimCtrNo');
    this.paramInsideVisitNo = this.route.snapshot.paramMap.get('insideVisitNo');

    this.usedSzza012aService.doSzza012a().pipe(
      takeUntil(this.onDestroy$),

      // ① 業務日付取得
      concatMap(response => {
        this.businessDate = response.todayDay;
        // ② 請求先住所情報取得（search が Observable を返す）
        return this.search();
      }),

      // ③ 支払方法変更可否チェック
      concatMap(() => {
        return this.usedSgkd002dSgkd048aService.select(this.paramClaimCtrNo, this.businessDate);
      })

    ).subscribe(response => {
      this.aceptAbleFlg = response.responseDto?.aceptAbleFlg;
      if (!this.aceptAbleFlg) {
        this.pushSgAriaMessage("ガス契約と同一供給地点の電気契約の支払方法は変更できません");
      }
    });

    // メインフォームの生成
    this.mainForm = this.createMainForm();
    // コード値読込
    this.loadCodeList();
    // クレジット企業コード読込
    this.loadCrdCompanycodeInfoList();
  }

  /**
   * 破棄処理
   * 
   * @memberof Sgkd002dComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  /**
   * メインFormの生成
   * 
   * @returns - メインForm
   * @memberof Sgkd002dComponent
   */
  private createMainForm(): UntypedFormGroup {
    return this.formBuilder.group({});
  }

  /**
   * コード値読込
   *
   * @memberof Sgkd002dComponent
   */
  private loadCodeList(): void {
    this.codeConversionMstService.getCodeConversionMst([
      // 支払方法
      SgKdConstants.CODE.PAYMETHOD.DISP_ITEM_ID,
      // 預金種別
      SgKdConstants.CODE.DPTCLSFY.DISP_ITEM_ID,
      // 受取方法
      SgKdConstants.CODE.RECIV_METHOD.DISP_ITEM_ID
    ]).pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response) => {
          this.codePayMethodList = this.sortedCodeSelectBox(response.get(SgKdConstants.CODE.PAYMETHOD.DISP_ITEM_ID));
          this.codeDptClsfyCdList = this.sortedCodeSelectBox(response.get(SgKdConstants.CODE.DPTCLSFY.DISP_ITEM_ID));
          this.codeRecivMethodList = this.sortedCodeSelectBox(response.get(SgKdConstants.CODE.RECIV_METHOD.DISP_ITEM_ID));
          this.loadedCodeList = true;
        }
    )
  }

  /**
   * クレジット企業コード読込
   *
   * @memberof Sgkd002dComponent
   */
  private loadCrdCompanycodeInfoList(): void {
    let tmpCodeCreComAnameList: SelectBox[] = [];
    this.usedSgkd002dSgkd044aService.doSgkd044a()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        response => {
          response.responseDto.crdCompanycodeInfoList.forEach((info) => {
            tmpCodeCreComAnameList.push({ value: info.crdCompanycode, viewValue: info.crdCompanyname} )
          });
          this.codeCreComAnameList = tmpCodeCreComAnameList;
          this.loadedCompanycodeInfoList = true;
        }, error => {
        });
  }

  /**
   * コードリストを項目並び順番でソートしたセレクトボックスを取得
   * @param array - コードリスト
   * @returns - 項目並び順番でソートされたコードリストのセレクトボックス
   *
   * @memberof Sgkd002dComponent
   */
  private sortedCodeSelectBox(array: Array<CodeConversionMstDto>): SelectBox[] {

    let returnArray: SelectBox[] = [];
    array
      .sort((x, y) => x.hyojiRowTurn.localeCompare(y.hyojiRowTurn))
      .forEach(x => returnArray.push({ value: x.userCode, viewValue: x.hyojiCont}));
    return returnArray;
  }

  /**
   * 請求先住所情報取得
   * 
   * @memberof Sgkd002dComponent
   */
  private search(): Observable<Sgkd034aResponse> {
    return this.usedSgkd002dSgkd034aService
      .doSgkd034a(this.paramClaimCtrNo, this.businessDate)
      .pipe(
        tap(response => {
          this.selectedSeikyuInfo = true;
          this.response = response;
        })
      );
  }

  /**
   * サブミット処理
   * 
   * @memberof Sgkd002dComponent
   */
  onSubmit(): void {

    const now = new Date();
    let time = String(now.getHours()).padStart(2, '0')
                 + String(now.getMinutes()).padStart(2, '0');
    let observable = null;

    this.clearMessage();

    switch (this.mainForm.get('selectedPayMethod').value) {
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.KOUZA: // 口座振替
        observable = this.usedSgkd002dSgkd004aService.addKouzaInfo(
          this.paramInsideVisitNo, this.paramClaimCtrNo, 
          this.businessDate, time, <UntypedFormGroup>this.mainForm.get('kouzaForm'));
        break;
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.CRD: // クレジットカード
        observable = this.usedSgkd002dSgkd004aService.addCreditInfo(
          this.paramInsideVisitNo, this.paramClaimCtrNo, 
          this.businessDate, time, <UntypedFormGroup>this.mainForm.get('creditForm'));
        break;
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.SMS: // ＳＭＳ決済
        observable = this.usedSgkd002dSgkd004aService.addSmsInfo(
          this.paramInsideVisitNo, this.paramClaimCtrNo, 
          this.businessDate, time, <UntypedFormGroup>this.mainForm.get('smsForm'));
        break;
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.CVS: //  ＣＶＳ払込票
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.SEIKYU: // 請求書払い
      case SgKdConstants.CODE.PAYMETHOD.USER_CODE.TOKUTEI: // 特定払込票
        observable = this.usedSgkd002dSgkd004aService.addCsvInfo(
          this.paramInsideVisitNo, this.paramClaimCtrNo,
          this.businessDate, time, this.mainForm.get('selectedPayMethod').value);
        break;
    }

    if (observable) {
      observable.pipe(
        takeUntil(this.onDestroy$),
        // 004Aの戻りを確認して005Aを呼出（同期呼出）
        concatMap((response1: Sgkd004aResponse) => {
          // 受付番号設定
          this.aceptNo = response1.responseDto?.aceptNo;
          switch (this.mainForm.get('selectedPayMethod').value) {
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.KOUZA: // 口座振替
              return this.usedSgkd002dSgkd005aService.addKouzaInfo(
                this.paramClaimCtrNo, this.businessDate, this.aceptNo,
                <UntypedFormGroup>this.mainForm.get('kouzaForm'));
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.CRD: // クレジットカード
              return this.usedSgkd002dSgkd005aService.addCreditInfo(
                this.paramClaimCtrNo, this.businessDate, this.aceptNo,
                <UntypedFormGroup>this.mainForm.get('creditForm'));
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.SMS: // ＳＭＳ決済
              return this.usedSgkd002dSgkd005aService.addSmsInfo(
                this.paramClaimCtrNo, this.businessDate, this.aceptNo,
                <UntypedFormGroup>this.mainForm.get('smsForm'));
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.CVS: //  ＣＶＳ払込票
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.SEIKYU: // 請求書払い
            case SgKdConstants.CODE.PAYMETHOD.USER_CODE.TOKUTEI: // 特定払込票
              return this.usedSgkd002dSgkd005aService.addCsvInfo(
                    this.paramClaimCtrNo, this.businessDate, this.aceptNo,
                    this.mainForm.get('selectedPayMethod').value);
          }
        })
      ).subscribe(
        response2 => {
          this.complete();
        }, error => {
        }
      );
    }

  }

  /**
   * メッセージ領域のクリア
   * 
   * @memberof Sgkd002dComponent
   */
  clearMessage(): void {
    this.errorMessageModelArray = [];
  }

  /**
   * インフォメッセージのメッセージエリアモデル作成
   * (メッセージエリア配列の再生成)
   * 
   * @param message インフォメッセージ
   * @memberof Sgkd002dComponent
   */
  pushSgAriaMessage(message: string): void {
    this.errorMessageModelArray = [
      ...this.errorMessageModelArray,
      {
        message: message,
        messageClass: SgKdConstants.MESSAGE.INFO.MESSAGE_CLS,
        icon: SgKdConstants.MESSAGE.INFO.ICON,
        iconClass: SgKdConstants.MESSAGE.INFO.ICON_CLS,
      }
    ];
  }

  /**
   * 情報アイコン押下処理
   * @memberof Sgkd002dComponent
   */
  onClickIcon() {
    // 履歴管理キー生成：請求お客さま番号 + "_" + 業務日付
    let hmKey = this.paramClaimCtrNo + "_" + this.businessDate;

    // 実行履歴情報画面モーダル表示
    this.dialog.open(Sgkz004dComponent, {
      disableClose: true, // 背景クリックのクローズ禁止
      data: {
        // 履歴管理グループ OT_PCKDSHH（支払情報）
        historyManagementGroup: 'OT_PCKDSHH',
        // 履歴管理キー： 請求お客さま番号/業務日付
        historyManagementKey: hmKey,
        // 承認欄表示フラグに'0'（承認者欄非表示）を設定
        apprvOnFlg: '0'
      },
    });
  }

  /**
   * 登録完了処理
   * 
   * @memberof Sgkd002dComponent
   */
  private complete(): void {
    this.completeDataService.completeData = {
      title: '支払情報変更登録完了',
      message: '支払情報変更を登録しました',
      next: '',
      // koumokuList: [
      //   { label: '受付番号', val: '' },
      // ],
    }
    this.router.navigate([SgKdConstants.PATH.COMPLETE]);
  }
}
